/**
 * QChatMessages Component (v3.0 - Bolt/Claude Inspired)
 * Professional chat messages with minimal spacing and crisp typography
 *
 * Key Improvements v3.0:
 * - Bolt.new inspired design (narrow spacing, inline timestamps)
 * - Crisp white text (#F5F5F5) on pure dark (#0A0A0F)
 * - No bubble for AI messages (cleaner)
 * - Minimal gap between message and timestamp
 * - Professional institutional style
 */

import React, { useRef, useEffect } from 'react';
import { Box, Typography, Avatar } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import QActionCard from './QActionCard';
import QActionChips from './QActionChips';

// Timestamp formatting removed - cleaner institutional design

/**
 * QChatMessages Component
 * @param {array} messages - Array of message objects
 * @param {function} onActionCard - Callback when action card is clicked
 * @param {function} onActionChip - Callback when action chip is clicked
 * @param {boolean} isLoading - Whether AI is currently responding
 */
const QChatMessages = ({ messages = [], onActionCard, onActionChip, isLoading = false }) => {
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  return (
    <Box
      sx={{
        flex: 1,
        overflowY: 'auto',
        padding: { xs: '8px 10px', md: '10px 14px' }, // Compact padding
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75, // 6px - minimal gap between messages
        backgroundColor: '#0A0A0F',
        '&::-webkit-scrollbar': {
          width: '3px'
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent'
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(255, 255, 255, 0.08)',
          borderRadius: '2px'
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: 'rgba(255, 255, 255, 0.15)'
        }
      }}
    >
      {messages.map((message) => (
        <MessageBubble
          key={message.id}
          message={message}
          onActionCard={onActionCard}
          onActionChip={onActionChip}
        />
      ))}

      {/* Typing indicator */}
      {isLoading && messages[messages.length - 1]?.role !== 'assistant' && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <QAvatar size="small" />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TypingDots />
            <Typography sx={{ color: '#71717A', fontSize: '0.8125rem' }}>
              Q is thinking...
            </Typography>
          </Box>
        </Box>
      )}

      <div ref={messagesEndRef} />
    </Box>
  );
};

/**
 * Q Avatar Component - Compact Bolt-style
 */
const QAvatar = ({ isError = false, size = 'default' }) => {
  const dimensions = size === 'small' ? 22 : 24; // Smaller avatar
  const fontSize = size === 'small' ? '0.625rem' : '0.6875rem';

  return (
    <Avatar
      sx={{
        width: dimensions,
        height: dimensions,
        backgroundColor: isError
          ? 'rgba(239, 68, 68, 0.1)'
          : 'rgba(39, 181, 109, 0.1)',
        color: isError
          ? '#EF4444'
          : '#27B56D',
        fontSize,
        fontWeight: 700,
        border: 'none',
        flexShrink: 0
      }}
    >
      Q
    </Avatar>
  );
};

/**
 * Typing Dots Animation - Bolt style
 */
const TypingDots = () => (
  <Box sx={{ display: 'flex', gap: 0.4 }}>
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        sx={{
          width: 4,
          height: 4,
          borderRadius: '50%',
          backgroundColor: '#27B56D',
          animation: 'typingDot 1.2s infinite',
          animationDelay: `${i * 0.15}s`,
          '@keyframes typingDot': {
            '0%, 60%, 100%': { opacity: 0.3 },
            '30%': { opacity: 1 }
          }
        }}
      />
    ))}
  </Box>
);

/**
 * Message Bubble Component - Compact Bolt-style
 * Clean design with inline timestamps and minimal spacing
 */
const MessageBubble = ({ message, onActionCard, onActionChip }) => {
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';

  // Detect error messages
  const isError = isAssistant && (
    message.content?.toLowerCase().includes('sorry') &&
    message.content?.toLowerCase().includes('error')
  );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1, // Tighter gap
        flexDirection: isUser ? 'row-reverse' : 'row'
      }}
    >
      {/* Avatar - Only for assistant */}
      {isAssistant && <QAvatar isError={isError} size="small" />}

      {/* Message Content */}
      <Box
        sx={{
          maxWidth: { xs: '90%', md: '75%' },
          display: 'flex',
          flexDirection: 'column',
          alignItems: isUser ? 'flex-end' : 'flex-start'
        }}
      >
        {/* Text Content */}
        <Box
          sx={{
            // User: subtle green bubble, Assistant: no bubble (clean)
            backgroundColor: isUser
              ? 'rgba(39, 181, 109, 0.06)'
              : isError
              ? 'rgba(239, 68, 68, 0.04)'
              : 'transparent',
            borderRadius: isUser ? '10px 10px 3px 10px' : '0',
            padding: isUser ? '8px 12px' : '0',
            border: isUser ? '1px solid rgba(39, 181, 109, 0.1)' : 'none'
          }}
        >
          {isAssistant ? (
            <Box
              sx={{
                color: '#E4E4E7', // Crisp text
                fontSize: '0.8125rem', // Smaller font
                lineHeight: 1.55,
                '& p': { margin: 0, marginBottom: '0.4em' },
                '& p:last-child': { marginBottom: 0 },
                '& strong': { color: '#F5F5F5', fontWeight: 600 },
                '& em': { color: '#A1A1AA', fontStyle: 'italic' },
                '& ul, & ol': { margin: '0.3em 0', paddingLeft: '1.1em' },
                '& li': { marginBottom: '0.15em' },
                '& code': {
                  backgroundColor: 'rgba(39, 181, 109, 0.1)',
                  color: '#27B56D',
                  padding: '1px 4px',
                  borderRadius: '3px',
                  fontSize: '0.85em',
                  fontFamily: "'JetBrains Mono', monospace"
                },
                '& pre': {
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  padding: '10px',
                  borderRadius: '5px',
                  overflow: 'auto',
                  margin: '0.4em 0',
                  '& code': {
                    backgroundColor: 'transparent',
                    color: '#D4D4D8',
                    padding: 0
                  }
                }
              }}
            >
              <ReactMarkdown>{message.content}</ReactMarkdown>
              {message.isStreaming && (
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    width: '2px',
                    height: '0.9em',
                    backgroundColor: '#27B56D',
                    marginLeft: '2px',
                    verticalAlign: 'text-bottom',
                    animation: 'blink 0.7s infinite',
                    '@keyframes blink': {
                      '0%, 50%': { opacity: 1 },
                      '51%, 100%': { opacity: 0 }
                    }
                  }}
                />
              )}
            </Box>
          ) : (
            <Typography
              sx={{
                color: '#E4E4E7',
                fontSize: '0.8125rem',
                lineHeight: 1.5
              }}
            >
              {message.content}
            </Typography>
          )}
        </Box>

        {/* Timestamp removed per institutional design - cleaner look */}

        {/* Action Cards (legacy) */}
        {message.actionCards && message.actionCards.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              marginTop: 0.75
            }}
          >
            {message.actionCards.map((card) => (
              <QActionCard
                key={card.id}
                id={card.id}
                icon={card.icon}
                title={card.title}
                desc={card.desc}
                onClick={onActionCard}
              />
            ))}
          </Box>
        )}

        {/* Action Chips (2x3 grid) - Tighter spacing */}
        {message.showActionChips && (
          <Box sx={{ marginTop: 1 }}>
            <QActionChips
              onAction={onActionChip}
              disabled={false}
              variant="grid"
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default QChatMessages;
